import DashboardNavigationBar from '@/components/dashboard/navigation-bar/dashboard-navigation-bar';
import {
  useCreateJunctionLinkV0WearablesJunctionLinkPost,
  useGetConnectedJunctionDevicesV0WearablesConnectedJunctionDevicesGet,
} from '@/api/generated-catch-backend/wearables/wearables';
import Loading from '@/components/loading/loading';
import { useState, useEffect } from 'react';

export default function WearablesPage() {
  const [copiedCode, setCopiedCode] = useState(false);

  // Check if we're in production environment
  const isProduction = import.meta.env.VITE_DEPLOYMENT_TARGET === 'PROD';

  const {
    mutate: createJunctionLink,
    data: linkData,
    isPending: isCreatingLink,
    isError: isLinkError,
  } = useCreateJunctionLinkV0WearablesJunctionLinkPost();

  const { data: devicesData, isLoading: isLoadingDevices } =
    useGetConnectedJunctionDevicesV0WearablesConnectedJunctionDevicesGet();

  // Create Junction link on mount to get the Apple Health code (only in non-production)
  useEffect(() => {
    if (!isProduction && !linkData && !isCreatingLink && !isLinkError) {
      createJunctionLink();
    }
  }, []); // Empty dependency array - only run once on mount

  const handleConnectJunctionWeb = () => {
    if (linkData?.linkWebUrl) {
      window.open(linkData.linkWebUrl, '_blank');
    }
  };

  const handleCopyCode = () => {
    if (linkData?.appleHealthCode) {
      navigator.clipboard.writeText(linkData.appleHealthCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  if (isProduction) {
    return (
      <>
        <DashboardNavigationBar activeItem="wearables" />
        <div className="w-full">
          <div className="container mx-auto px-4 py-8">
            <div className="mx-auto max-w-4xl">
              <h1 className="mb-6 text-3xl font-bold text-gray-900">Wearables</h1>
              <div className="rounded-lg bg-white p-8 text-center shadow-md">
                <div className="mb-4 text-6xl">
                  <span role="img" aria-label="rocket ship">
                    🚀
                  </span>
                </div>
                <h2 className="mb-3 text-2xl font-semibold text-gray-900">This functionality is coming soon!</h2>
                <p className="text-gray-600">
                  We're working hard to bring you the ability to connect your wearable devices and sync your health
                  data. Check back soon for updates!
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (isLoadingDevices) {
    return (
      <>
        <DashboardNavigationBar activeItem="wearables" />
        <Loading />
      </>
    );
  }

  const connectedDevices = devicesData?.providers || [];
  const hasConnectedDevices = connectedDevices.length > 0;

  return (
    <>
      <DashboardNavigationBar activeItem="wearables" />
      <div className="w-full">
        <div className="container mx-auto px-4 py-8">
          <div className="mx-auto max-w-4xl">
            <h1 className="mb-6 text-3xl font-bold text-gray-900">Wearables</h1>

            {/* Connected Devices Section */}
            {hasConnectedDevices && (
              <div className="mb-6 rounded-lg bg-white p-6 shadow-md">
                <h2 className="mb-4 text-xl font-semibold">Connected Devices</h2>
                <div className="space-y-3">
                  {connectedDevices.map((device) => (
                    <div
                      key={device.slug}
                      className="flex items-center justify-between rounded-lg border border-gray-200 p-4"
                    >
                      <div className="flex items-center space-x-4">
                        {device.logo && (
                          <img
                            src={device.logo}
                            alt={`${device.name} logo`}
                            className="h-10 w-10 rounded-lg object-contain"
                          />
                        )}
                        <div>
                          <h3 className="font-medium text-gray-900">{device.name}</h3>
                          <p className="text-sm text-gray-500">
                            Status:{' '}
                            <span
                              className={`font-medium ${
                                device.status === 'connected' ? 'text-green-600' : 'text-gray-600'
                              }`}
                            >
                              {device.status}
                            </span>
                          </p>
                          {device.createdOn && (
                            <p className="text-xs text-gray-400">
                              Connected on: {new Date(device.createdOn.toString()).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Connect New Device Section - Two Column Layout */}
            <div className="rounded-lg bg-white p-6 shadow-md">
              <h2 className="mb-4 text-xl font-semibold">
                {hasConnectedDevices ? 'Connect Another Device' : 'Connect Your Devices'}
              </h2>
              <p className="mb-6 text-gray-600">
                Choose how you'd like to connect your wearable devices and health data.
              </p>

              <div className="grid gap-6 md:grid-cols-2">
                {/* Junction Web Connect Column */}
                <div className="rounded-lg border border-gray-200 p-5">
                  <h3 className="mb-3 text-lg font-semibold">Junction Web Connect</h3>
                  <p className="mb-4 text-sm text-gray-600">
                    Connect Fitbit, Garmin, Oura, and other popular wearables through your web browser.
                  </p>
                  <button
                    onClick={handleConnectJunctionWeb}
                    disabled={isCreatingLink || !linkData}
                    className="w-full rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                  >
                    {isCreatingLink ? 'Preparing...' : 'Connect via Browser'}
                  </button>
                  {isLinkError && (
                    <div className="mt-3 rounded-md bg-red-50 p-2 text-sm text-red-700">
                      Failed to create connection link.
                    </div>
                  )}
                </div>

                {/* Vital Connect iOS App Column */}
                <div className="rounded-lg border border-gray-200 p-5">
                  <h3 className="mb-3 text-lg font-semibold">Vital Connect iOS App</h3>
                  <p className="mb-4 text-sm text-gray-600">
                    Connect Apple Health data directly from your iPhone using the Vital Connect app.
                  </p>

                  <div className="space-y-3">
                    <div>
                      <p className="mb-2 text-xs font-medium text-gray-700">Your Connection Code:</p>
                      <div className="flex items-center space-x-2">
                        <code className="flex-1 rounded bg-gray-100 px-3 py-2 text-center font-mono text-sm">
                          {linkData?.appleHealthCode || 'Loading...'}
                        </code>
                        <button
                          onClick={handleCopyCode}
                          disabled={!linkData?.appleHealthCode}
                          className="rounded bg-gray-200 px-3 py-2 text-sm hover:bg-gray-300 disabled:opacity-50"
                        >
                          {copiedCode ? '✓' : 'Copy'}
                        </button>
                      </div>
                    </div>

                    <div>
                      <p className="mb-2 text-xs text-gray-600">
                        1. Download Vital Connect from the App Store
                        <br />
                        2. Enter this code in the app
                        <br />
                        3. Grant access to Apple Health data
                      </p>
                      <a
                        href="https://apps.apple.com/us/app/vital-connect/6444167196"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block rounded-lg bg-black px-4 py-2 text-sm text-white hover:bg-gray-800"
                      >
                        Download on App Store →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
